import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import logger from '../../utils/logger';
import {
  useMeeting,
  useParticipant,
  VideoPlayer,
} from "@videosdk.live/react-sdk";
import { nameTructed } from "../../utils/helper";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { CornerDisplayName } from "../../components/ParticipantView";
import { useSiteContext } from "../../context/SiteContext";
import { useParticipantRanking } from "../../utils/participantRanking";
import { computeDynamicLayout } from "../../utils/dynamicLayout";
import { useBreakoutRooms } from "../../context/BreakoutRoomsContext";
// Component to manage pause/resume for participant streams based on focus
function FocusStreamController({ participantId, isActive }) {
  const { webcamStream, stopConsumingWebcamStreams, consumeWebcamStreams } =
    useParticipant(participantId);

  useEffect(() => {
    if (webcamStream) {
      try {
        if (!isActive) {
          stopConsumingWebcamStreams();
        } else if (isActive) {
          consumeWebcamStreams();
        }
      } catch (error) {
        logger("error", `Error controlling stream for ${participantId}:`, error);
      }
    }
  }, [
    isActive,
    webcamStream,
    stopConsumingWebcamStreams,
    consumeWebcamStreams,
    participantId,
  ]);

  return null;
}

function FocusParticipantView({ isPresenting }) {
  const {
    participants,
    pinnedParticipants,
    localParticipant,
    localScreenShareOn,
    presenterId,
  } = useMeeting();
  const { hideSelfView, localPinnedParticipant } = useSiteContext();
  const { activeSpeakerId } = useMeetingAppContext();
  const { isInSameRoom, currentRoom, participantRooms } = useBreakoutRooms();

  const [currentFocusParticipant, setCurrentFocusParticipant] = useState(null);
  const [speakerHistory, setSpeakerHistory] = useState([]);
  const speakerTimeoutRef = useRef(null);
  const currentFocusRef = useRef(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [lastFocusParticipant, setLastFocusParticipant] = useState(null);

  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Helper function to get participant data from Map
  const getParticipantData = (participantId) => {
    return participants.get(participantId);
  };

  // Helper function to log all participants
  const logAllParticipants = () => {
    logger("log", "=== All Participants ===");
    participants.forEach((participant, participantId) => {
      logger("log", `ID: ${participantId}`, participant);
    });
    logger("log", "========================");
  };

  const baseParticipantIds = useMemo(() => {
    const pinnedParticipantId = [...pinnedParticipants.keys()].filter(
      (participantId) => {
        return participantId !== localParticipant.id;
      }
    );
    const regularParticipantIds = [...participants.keys()].filter(
      (participantId) => {
        return (
          ![...pinnedParticipants.keys()].includes(participantId) &&
          localParticipant.id !== participantId
        );
      }
    );

    let ids = [
      localParticipant.id,
      ...pinnedParticipantId,
      ...regularParticipantIds,
    ];

    if (hideSelfView) {
      ids = ids.filter((id) => id !== localParticipant.id);
    }

    // Filter by breakout room - hide participants assigned to rooms from main meeting
    // Only show participants in the same room when in a breakout room
    // Host view should be same like other participant screen - no special treatment
    ids = ids.filter((participantId) => {
      // Use isInSameRoom for all participants including host/self
      // This ensures host is treated the same as other participants
      return isInSameRoom?.(participantId) ?? true;
    });

    return ids;
  }, [
    participants,
    pinnedParticipants,
    presenterId,
    localScreenShareOn,
    hideSelfView,
    currentRoom,
    isInSameRoom,
    participantRooms,
    localParticipant.id,
    activeSpeakerId,
  ]);

  const pinnedIds = useMemo(() => {
    try {
      const ids = Array.from(pinnedParticipants.keys());
      if (Array.isArray(localPinnedParticipant)) {
        ids.push(...localPinnedParticipant);
      }
      return ids;
    } catch (e) {
      return Array.isArray(localPinnedParticipant)
        ? localPinnedParticipant
        : [];
    }
  }, [pinnedParticipants, localPinnedParticipant]);

  const { orderedIds: participantIds, dominantId } = useParticipantRanking({
    participantIds: baseParticipantIds,
    participants,
    activeSpeakerId,
    pinnedIds,
    selfId: localParticipant?.id,
    // Disable dynamic
    enabled: false,
    config: { reorderIntervalMs: 800 },
  });

  // Initialize with first participant
  useEffect(() => {
    logger("log", "participantIds", participantIds);
    logAllParticipants(); // Log all participants for debugging
    if (participantIds.length > 0 && !currentFocusRef.current) {
      const firstParticipant = participantIds[0];
      const participant = getParticipantData(firstParticipant);
      logger("log", "First participant data:", participant);
      const firstParticipantObj = {
        id: firstParticipant,
        name:
          participant?.displayName ||
          participant?.name ||
          `Participant ${firstParticipant.slice(-4)}`,
        isLocal: firstParticipant === localParticipant?.id,
      };
      setCurrentFocusParticipant(firstParticipantObj);
      currentFocusRef.current = firstParticipantObj;
    }
  }, [participantIds, participants, localParticipant]);

  useEffect(() => {
    if (
      hideSelfView &&
      currentFocusParticipant &&
      currentFocusParticipant.id === localParticipant.id
    ) {
      logger("log", "Last focus participant:", lastFocusParticipant);
      if (lastFocusParticipant) {
        const participant = getParticipantData(lastFocusParticipant);
        setCurrentFocusParticipant({
          id: lastFocusParticipant,
          name:
            participant?.displayName ||
            participant?.name ||
            `Participant ${lastFocusParticipant.slice(-4)}`,
          isLocal: lastFocusParticipant === localParticipant?.id,
        });
        currentFocusRef.current = lastFocusParticipant;
      } else {
        const nextParticipant = participantIds.find(
          (id) => id !== localParticipant.id
        );
        if (nextParticipant) {
          const participant = getParticipantData(nextParticipant);
          setCurrentFocusParticipant({
            id: nextParticipant,
            name:
              participant?.displayName ||
              participant?.name ||
              `Participant ${nextParticipant.slice(-4)}`,
            isLocal: false,
          });
          currentFocusRef.current = nextParticipant;
        }
      }
    }
  }, [hideSelfView, participantIds, participants, localParticipant]);

  // Track active speaker changes and rotate focus
  useEffect(() => {
    const candidateId = dominantId;
    logger("log", 
      "Focus candidate changed:",
      candidateId,
      "Current focus:",
      currentFocusRef.current?.id
    );

    if (candidateId && candidateId !== currentFocusRef.current?.id) {
      if (hideSelfView && candidateId === localParticipant.id) {
        return;
      }
      const participant = getParticipantData(candidateId);
      logger("log", "Found participant:", participant);

      if (participant) {
        const newFocusParticipant = {
          id: candidateId,
          name:
            participant.displayName ||
            participant.name ||
            `Participant ${candidateId.slice(-4)}`,
          isLocal: candidateId === localParticipant?.id,
          timestamp: Date.now(),
        };

        logger("log", "Switching to new focus participant:", newFocusParticipant);

        // Update current focus participant
        if (currentFocusRef.current?.id !== lastFocusParticipant) {
          setLastFocusParticipant(currentFocusRef.current?.id);
        }
        setCurrentFocusParticipant(newFocusParticipant);
        currentFocusRef.current = newFocusParticipant;
      }
    }
  }, [
    dominantId,
    participants,
    localParticipant,
    hideSelfView,
    lastFocusParticipant,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (speakerTimeoutRef.current) {
        clearTimeout(speakerTimeoutRef.current);
      }
    };
  }, []);

  const containerRefCallback = useCallback((node) => {
    if (node) {
      containerRef.current = node;
    }
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerDimensions((prev) => {
        if (prev.width === rect.width && prev.height === rect.height)
          return prev;
        return { width: rect.width, height: rect.height };
      });
    };

    update();
    raf = window.requestAnimationFrame(() => update());

    if (!window.ResizeObserver) {
      window.addEventListener("resize", update);
      return () => {
        window.cancelAnimationFrame(raf);
        window.removeEventListener("resize", update);
      };
    }

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [containerRef.current]);

  return (
    <div className="flex flex-1 bg-transparent relative p-0 min-w-0 h-full">
      {/* Render stream controllers for all participants - pause streams not in focus */}
      {participantIds.map((participantId) => {
        const isActive = currentFocusParticipant?.id === participantId;
        return (
          <FocusStreamController
            key={`focus_stream_controller_${participantId}`}
            participantId={participantId}
            isActive={isActive}
          />
        );
      })}
      {/* Main Focus Area */}
      <div
        ref={containerRefCallback}
        className="flex-1 flex items-center justify-center min-w-0 w-full h-full"
      >
        {currentFocusParticipant ? (
          (() => {
            const aspectRatio = 16 / 9;
            const padding = 0;

            if (
              containerDimensions.width === 0 ||
              containerDimensions.height === 0
            ) {
              return (
                <div className="w-full h-full">
                  <FocusParticipantVideo
                    participantId={currentFocusParticipant.id}
                    isMainSpeaker={true}
                    displayName={currentFocusParticipant.name}
                    isLocal={currentFocusParticipant.isLocal}
                    mouseOver={mouseOver}
                    setMouseOver={setMouseOver}
                    isActiveSpeaker={true}
                  />
                </div>
              );
            }

            const layoutResult = computeDynamicLayout({
              participants: [{ id: currentFocusParticipant.id, aspectRatio }],
              containerWidth: containerDimensions.width,
              containerHeight: containerDimensions.height,
              gap: 0,
              padding: padding,
              minRowHeight: 90,
              maxRows: 1,
            });

            if (layoutResult.items.length === 0) {
              return (
                <div className="w-full h-full">
                  <FocusParticipantVideo
                    participantId={currentFocusParticipant.id}
                    isMainSpeaker={true}
                    displayName={currentFocusParticipant.name}
                    isLocal={currentFocusParticipant.isLocal}
                    mouseOver={mouseOver}
                    setMouseOver={setMouseOver}
                    isActiveSpeaker={true}
                  />
                </div>
              );
            }

            const item = layoutResult.items[0];

            return (
              <div
                className="absolute"
                style={{
                  left: `${item.x}px`,
                  top: `${item.y}px`,
                  width: `${item.width}px`,
                  height: `${item.height}px`,
                  minWidth: 0,
                  minHeight: 0,
                }}
              >
                <FocusParticipantVideo
                  participantId={currentFocusParticipant.id}
                  isMainSpeaker={true}
                  displayName={currentFocusParticipant.name}
                  isLocal={currentFocusParticipant.isLocal}
                  mouseOver={mouseOver}
                  setMouseOver={setMouseOver}
                  isActiveSpeaker={true}
                />
              </div>
            );
          })()
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-gray-400">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-700 flex items-center justify-center">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                </svg>
              </div>
              <p className="text-sm">Waiting for participants to join</p>
            </div>
          </div>
        )}
      </div>

      {/* Speaker History Sidebar */}
      {speakerHistory.length > 0 && (
        <div className="absolute right-12 top-12 w-48 bg-black bg-opacity-70 rounded-lg p-4">
          <h4 className="text-white text-sm font-medium mb-2">
            Recent Speakers
          </h4>
          <div className="space-y-2">
            {speakerHistory.slice(0, 5).map((speaker, index) => (
              <div
                key={speaker.id}
                className={`flex items-center gap-2 text-xs ${index === 0 ? "text-green-400" : "text-gray-300"
                  }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${index === 0 ? "bg-green-400" : "bg-gray-400"
                    }`}
                ></div>
                <span>{nameTructed(speaker.name, 15)}</span>
                {speaker.isLocal && (
                  <span className="text-gray-500">(You)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Focus Participant Corner Display Component
const FocusParticipantCornerDisplay = ({
  participantId,
  displayName,
  isLocal,
  mouseOver,
  isActiveSpeaker,
}) => {
  const { micOn, webcamOn } = useParticipant(participantId);

  return (
    <CornerDisplayName
      participantId={participantId}
      isPresenting={false}
      displayName={displayName}
      isLocal={isLocal}
      micOn={micOn}
      webcamOn={webcamOn}
      mouseOver={mouseOver}
      isActiveSpeaker={isActiveSpeaker}
    />
  );
};

// Individual Focus Participant Video Component
const FocusParticipantVideo = ({
  participantId,
  isMainSpeaker,
  displayName: propDisplayName,
  isLocal: propIsLocal,
  mouseOver,
  setMouseOver,
  isActiveSpeaker,
}) => {
  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    displayName,
    isLocal,
    metaData,
  } = useParticipant(participantId);
  const videoRef = useRef(null);
  const micRef = useRef(null);

  const finalDisplayName = propDisplayName || displayName;
  const finalIsLocal = typeof propIsLocal === "boolean" ? propIsLocal : isLocal;

  useEffect(() => {
    if (webcamStream && videoRef.current) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      videoRef.current.srcObject = mediaStream;
    }
  }, [webcamStream]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch((error) =>
            logger("error", "micRef.current.play() failed", error)
          );
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn, micRef]);

  return (
    <div
      onMouseEnter={() => setMouseOver && setMouseOver(true)}
      onMouseLeave={() => setMouseOver && setMouseOver(false)}
      className={`select-none w-full h-full relative overflow-hidden ${isActiveSpeaker ? "border-2 border-[#7700ff]" : ""
        }`}
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        backgroundColor: "transparent",
      }}
    >
      {/* Audio Stream */}
      <audio
        ref={micRef}
        autoPlay
        muted={finalIsLocal}
        style={{ display: "none" }}
      />
      {/* Video Stream */}
      {webcamOn && webcamStream ? (
        <div className="absolute inset-0 w-full h-full bg-black">
          {/* <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={finalIsLocal}
            className="w-full h-full"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scaleX(-1)",
              WebkitTransform: "scaleX(-1)",
            }}
          /> */}
          <VideoPlayer
            participantId={participantId}
            type="video"
            containerStyle={{
              height: "100%",
              width: "100%",
            }}
            className="h-full w-full"
            classNameVideo="h-full w-full"
            videoStyle={{
              objectFit: "contain",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
      ) : (
        <div className="h-full w-full flex items-center justify-center">
          <div
            className={`z-10 flex items-center justify-center rounded-full bg-gray-800 h-[52px] w-[52px]`}
          >
            {metaData?.profileImage ? (
              <img
                alt="Profile3"
                className="w-full h-full rounded-full object-cover"
                src={metaData?.profileImage}
              />
            ) : (
              <span className="text-2xl text-white">
                {String(finalDisplayName).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Corner Display Name */}
      {webcamOn && webcamStream ? (
        <div className="absolute inset-0 pointer-events-none">
          <FocusParticipantCornerDisplay
            participantId={participantId}
            displayName={finalDisplayName}
            isLocal={finalIsLocal}
            mouseOver={mouseOver}
            isActiveSpeaker={isActiveSpeaker}
          />
        </div>
      ) : (
        <FocusParticipantCornerDisplay
          participantId={participantId}
          displayName={finalDisplayName}
          isLocal={finalIsLocal}
          mouseOver={mouseOver}
          isActiveSpeaker={isActiveSpeaker}
        />
      )}
    </div>
  );
};

const MemorizedParticipantView = React.memo(
  FocusParticipantView,
  (prevProps, nextProps) => {
    return prevProps.isPresenting === nextProps.isPresenting;
  }
);

export default MemorizedParticipantView;
